import { buttonVariants } from '@worldcoo/ui/components/button';
import { cn } from '@worldcoo/ui/lib/utils';
import { Logo } from './logo';
import { useLingui } from '@lingui/react/macro';
import { Link, isNotFound, useRouteContext, useSearch } from '@tanstack/react-router';
import { lazy, Suspense, type ReactNode } from 'react';
import { usePortalMetadataQuery } from '@/lib/data-hooks';
import { usePortalDisplayContext } from './portal-display-context';
import { useIsMobile } from '@worldcoo/ui/hooks/use-mobile';
import { MenuIcon } from 'lucide-react';
import { LanguageSelect } from './language-select';

const MobileNav = lazy(() => import('./header-mobile-nav').then((m) => ({ default: m.MobileNav })));

export function Header() {
	const portalData = usePortalDisplayContext();
	if (portalData) {
		return (
			<HeaderContent
				logo={
					<div className="max-w-[300px] flex-1">
						<Logo />
					</div>
				}
			/>
		);
	}
	return (
		<Suspense>
			<SuspensedHeader />
		</Suspense>
	);
}

function HeaderContent(props: { logo: ReactNode; actions?: ReactNode }) {
	return (
		<div className="bg-background border-border sticky top-0 z-20 border-b">
			<header className="mx-auto flex h-20 max-w-[1366px] items-center justify-between gap-3 px-6">
				{props.logo}
				{props.actions}
			</header>
		</div>
	);
}

function SuspensedHeader() {
	const { portalId } = useRouteContext({ from: '__root__' });
	const { t } = useLingui();
	const { iframe } = useSearch({ from: '__root__' });
	const portalMetadata = usePortalMetadataQuery();
	const isMobile = useIsMobile();

	if (iframe) return;
	if (isNotFound(portalMetadata)) return null;

	return (
		<HeaderContent
			logo={
				/* Limit logo here for cases where the logo itself has a weird aspect-ratio like comerciaglobalpayments */
				<Link
					to="/$portalId"
					params={{ portalId }}
					onClick={() => window.scrollTo(0, 0)}
					className="max-w-[300px] flex-1"
				>
					<Logo />
				</Link>
			}
			actions={
				<>
					<div className="hidden items-center gap-3 py-3 sm:flex">
						{!portalMetadata.hideAppLink && (
							<a
								href="https://app.worldcoo.com"
								target="_blank"
								rel="noopener noreferrer"
								className={cn(buttonVariants({ variant: 'ghost', size: '2xl' }), 'font-normal')}
							>
								{t`Donation Certificate`}
							</a>
						)}

						<LanguageSelect />
					</div>

					{isMobile && (
						<Suspense
							fallback={
								<button type="button" className="ml-auto">
									<MenuIcon className="size-8" />
								</button>
							}
						>
							<MobileNav hideAppLink={portalMetadata.hideAppLink} />
						</Suspense>
					)}
				</>
			}
		/>
	);
}
